import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Mail, Phone, Building2, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Panel, Empty, Chip, ConfirmDelete } from "../components/common";
import { DemandeFormDialog } from "../components/DemandeFormDialog";
import { RdvFormDialog } from "../components/RdvFormDialog";
import { MissionFormDialog } from "../components/MissionFormDialog";
import { RdvItem } from "../components/RdvItem";
import { MissionCard } from "../components/MissionCard";
import { EchangesTimeline } from "../components/EchangesTimeline";
import { useData } from "../context/DataContext";
import { ETAPES, fmtDate, fmtEUR } from "../lib/constants";

const Stepper = ({ d }) => {
  const { patch } = useData();
  const idx = ETAPES.findIndex((e) => e.id === d.etape);
  const go = async (id) => { await patch("demandes", d.id, { etape: id }); toast.success("Étape mise à jour"); };
  return (
    <div className="kc-card flex flex-wrap gap-2 p-2" data-testid="stage-stepper">
      {ETAPES.map((e, i) => {
        const active = e.id === d.etape;
        const done = e.id !== "perdu" && d.etape !== "perdu" && i < idx;
        return (
          <button key={e.id} onClick={() => go(e.id)} data-testid={`stage-step-${e.id}`}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${active ? "text-white" : done ? "bg-[var(--kc-sand)] text-[var(--kc-navy)]" : "text-[var(--kc-muted)] hover:bg-[var(--kc-sand)]"}`}
            style={active ? { background: e.color } : undefined}>
            {done && <Check className="h-3.5 w-3.5" />}{e.label}
          </button>
        );
      })}
    </div>
  );
};

const Info = ({ label, children }) => (
  <div><p className="kc-eyebrow mb-1">{label}</p><div className="text-sm text-[var(--kc-ink)]">{children || "—"}</div></div>
);

export default function DemandeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { byId, rdvs, missions, remove, loading } = useData();
  const [edit, setEdit] = useState(false);
  const [rdvDlg, setRdvDlg] = useState({ open: false, item: null });
  const [misDlg, setMisDlg] = useState({ open: false, item: null });
  const d = byId[id];

  if (loading) return <p className="text-[var(--kc-muted)]">Chargement…</p>;
  if (!d) return <Empty>Demande introuvable. <Link to="/demandes" className="underline">Retour aux demandes</Link></Empty>;

  const myRdvs = rdvs.filter((r) => r.demande_id === id).sort((a, b) => Number(a.fait) - Number(b.fait) || a.date.localeCompare(b.date));
  const myMissions = missions.filter((m) => m.demande_id === id);
  const del = async () => { await remove("demandes", id); toast.success("Demande supprimée"); navigate("/demandes"); };

  return (
    <div data-testid="demande-detail-page">
      <Link to="/demandes" className="mb-6 inline-flex items-center gap-1 text-sm text-[var(--kc-muted)] hover:text-[var(--kc-navy)]" data-testid="back-to-demandes-link"><ArrowLeft className="h-4 w-4" />Demandes</Link>
      <div className="kc-fade mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="kc-eyebrow mb-2">{d.secteur || "Secteur à préciser"} · {d.taille}</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[var(--kc-navy)] sm:text-4xl" data-testid="demande-detail-entreprise">{d.entreprise}</h1>
          <p className="mt-1 text-sm text-[var(--kc-muted)]">Demande reçue le {fmtDate(d.created_at)} · origine : {d.origine}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEdit(true)} data-testid="demande-edit-button"><Pencil className="mr-1 h-4 w-4" />Modifier</Button>
          <ConfirmDelete onConfirm={del} title="Supprimer cette demande ?" description="Les rendez-vous, missions et échanges associés seront également supprimés."
            trigger={<Button variant="outline" className="text-[#B42318]" data-testid="demande-delete-button"><Trash2 className="h-4 w-4" /></Button>} />
        </div>
      </div>

      <Stepper d={d} />

      <div className="mt-8 grid gap-6 xl:grid-cols-3">
        <div className="space-y-6">
          <Panel title="Fiche demande" testId="demande-fiche-panel">
            <div className="space-y-5">
              <Info label="Contact"><p className="font-medium">{d.contact_nom}</p><p className="text-[var(--kc-muted)]">{d.contact_poste}</p></Info>
              <div className="space-y-2 text-sm">
                {d.contact_email && <a href={`mailto:${d.contact_email}`} className="flex items-center gap-2 text-[var(--kc-navy)] hover:underline" data-testid="demande-email-link"><Mail className="h-4 w-4 text-[var(--kc-muted)]" />{d.contact_email}</a>}
                {d.contact_tel && <a href={`tel:${d.contact_tel}`} className="flex items-center gap-2 text-[var(--kc-navy)] hover:underline" data-testid="demande-tel-link"><Phone className="h-4 w-4 text-[var(--kc-muted)]" />{d.contact_tel}</a>}
                <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-[var(--kc-muted)]" />{d.entreprise}</p>
              </div>
              <Info label="Besoin"><div className="flex flex-wrap gap-1.5">{d.besoins.length ? d.besoins.map((b) => <Chip key={b}>{b}</Chip>) : "À préciser"}</div></Info>
              <div className="grid grid-cols-2 gap-4"><Info label="Origine">{d.origine}</Info><Info label="Montant estimé"><span className="font-mono">{fmtEUR(d.montant_estime)} HT</span></Info></div>
              <Info label="Description">{d.description}</Info>
            </div>
          </Panel>
          <Panel title="RDV & relances" testId="demande-rdvs-panel"
            action={<Button size="sm" variant="outline" onClick={() => setRdvDlg({ open: true, item: null })} data-testid="demande-add-rdv-button"><Plus className="mr-1 h-4 w-4" />Planifier</Button>}>
            <div className="space-y-2.5">
              {myRdvs.map((r) => <RdvItem key={r.id} rdv={r} showClient={false} onEdit={(item) => setRdvDlg({ open: true, item })} />)}
              {!myRdvs.length && <Empty>Aucun rendez-vous ni relance.</Empty>}
            </div>
          </Panel>
        </div>

        <Panel className="xl:col-span-2" title="Historique des échanges" testId="demande-echanges-panel">
          <EchangesTimeline demandeId={id} />
        </Panel>
      </div>

      <div className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-[var(--kc-navy)]">Missions</h2>
          <Button size="sm" className="kc-btn-primary" onClick={() => setMisDlg({ open: true, item: null })} data-testid="demande-add-mission-button"><Plus className="mr-1 h-4 w-4" />Créer une mission</Button>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {myMissions.map((m) => <MissionCard key={m.id} mission={m} showClient={false} onEdit={(item) => setMisDlg({ open: true, item })} />)}
        </div>
        {!myMissions.length && <Empty>Aucune mission pour ce client. Créez-en une dès la signature.</Empty>}
      </div>

      <DemandeFormDialog open={edit} onOpenChange={setEdit} initial={d} />
      <RdvFormDialog open={rdvDlg.open} onOpenChange={(o) => setRdvDlg((s) => ({ ...s, open: o }))} initial={rdvDlg.item} demandeId={id} />
      <MissionFormDialog open={misDlg.open} onOpenChange={(o) => setMisDlg((s) => ({ ...s, open: o }))} initial={misDlg.item} demandeId={id} defaultType={d.besoins[0]} />
    </div>
  );
}
